import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Icon } from '../../../../components/common/icon/Icon';
import { PopupConfirmModal } from '../../../../components/common/modal/popup/PopupConfirmModal';
import { useRedirectToStoredPage } from '../../../../hooks/page/useRedirectToStoredPage';
import { usePopup } from '../../../../hooks/popup/usePopup';
import { useLayout } from '../../../../hooks/screen/useLayout';
import { useAppDispatch, useAppSelector } from '../../../../hooks/store/useStore';
import { userJoinSlice } from '../../../../store/userJoinSlice';

interface JoinProcessPageWrapperProps {
  headerTitle: string;
  description?: string;
  children: JSX.Element;
  canConfirm: boolean;
  confirmHandler?: () => void;
}

export const JoinProcessPageWrapper = ({
  headerTitle,
  description,
  children,
  canConfirm,
  confirmHandler,
}: JoinProcessPageWrapperProps) => {
  const location = useLocation();
  const Popup = usePopup();
  const navigate = useNavigate();

  const [isCloseButtonClicked, setIsCloseButtonClicked] = useState<boolean>(false);

  const userJoinSelector = useAppSelector((state) => state.userJoin);
  const dispatch = useAppDispatch();
  const userJoinActions = userJoinSlice.actions;

  const { navigateToStoredPage } = useRedirectToStoredPage();

  useLayout({
    fixedHeader: (
      <CloseButtonSection>
        <CloseButton onClick={() => setIsCloseButtonClicked(true)}>
          <Icon name={'CloseGrey'} width={20} height={20} />
        </CloseButton>
      </CloseButtonSection>
    ),
    fixedFooter: (
      <ButtonSection>
        <BlurredSection />
        <ConfirmButton
          type={'button'}
          disabled={!canConfirm}
          onClick={() => {
            if (confirmHandler) {
              confirmHandler();
            }
          }}
        >
          <ConfirmButtonText>{'다음'}</ConfirmButtonText>
        </ConfirmButton>
      </ButtonSection>
    ),
    fixedHeaderHeight: 40,
    fixedFooterHeight: 72,
    shouldShowFooter: false,
  });

  useEffect(() => {
    if (location.pathname !== '/join-process/agreement') {
      if (!userJoinSelector.isJoinProcessStartedProperly) {
        Popup.toast('비정상적인 접근입니다.');
        navigate('/oauth/login');
      }
    }
  }, [location.pathname, userJoinSelector.isJoinProcessStartedProperly, Popup, navigate]);

  return (
    <>
      <Container>
        <TopSection>
          <HeaderSection>
            <Header>{headerTitle}</Header>
            {description && <Description>{description}</Description>}
          </HeaderSection>

          {children}
        </TopSection>
      </Container>

      <PopupConfirmModal
        isShow={isCloseButtonClicked}
        hasCloseButton={false}
        closeModal={() => setIsCloseButtonClicked(false)}
        description={'회원 가입이 취소돼요!\n절차를 취소하시겠어요?'}
        buttonArray={[
          {
            text: '이전 페이지로 돌아가기',
            onClick: () => {
              dispatch(userJoinActions.changeIsJoinProcessStartedProperly(false));
              navigateToStoredPage();
            },
            color: 'blue',
          },
          {
            text: '닫기',
            onClick: () => setIsCloseButtonClicked(false),
            color: 'gray',
          },
        ]}
      />
    </>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;

  width: 100%;
  height: 100vh;

  padding: 0px 24px 32px 24px;

  gap: 32px;
`;

const TopSection = styled.div`
  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;

  gap: 32px;

  overflow: hidden;
`;

const CloseButtonSection = styled.div`
  width: 100%;
  height: 40px;

  display: flex;
  justify-content: flex-end;
  align-items: center;

  padding: 0px var(--radius-large, 16px);
`;

const CloseButton = styled.button``;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  gap: 16px;
`;

const Header = styled.p`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/XX-Large/xxl-bold */
  font-family: Pretendard;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 31.2px */

  word-break: break-all;
`;

const Description = styled.pre`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);
  text-align: center;

  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 20.8px */
`;

const ConfirmButton = styled.button`
  width: 100%;
  height: 40px;
  max-height: 40px;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: 16px;

  gap: 4px;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Color-primary-blue, #196eff);

  &:disabled {
    opacity: 0.3;
  }

  cursor: pointer;
`;

const ConfirmButtonText = styled.span`
  color: var(--Light-Scheme-Monochrome-white, #fff);

  /* System/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 11.9px */
`;

const ButtonSection = styled.div`
  position: relative;
  display: flex;
  width: 100%;

  padding: 0px 16px 32px 16px;
  background-color: white;

  justify-content: center;
  align-items: center;

  gap: 8px;
`;

const BlurredSection = styled.div`
  width: 100%;
  height: 40px;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: var(--radius-large, 16px);
  gap: var(--radius-small, 4px);

  border-radius: var(--radius-none, 0px);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 63.96%);

  position: absolute;
  top: -40px;
`;
